import React from 'react'

import Button from '../form/Button'
import MicrophoneIcon from '../../../assets/icons/microphone.svg?react'
import SendIcon from '../../../assets/icons/send.svg?react'
import CloseIcon from '../../../assets/icons/close.svg?react'

import VoiceActivityBars from './VoiceActivityBars'
import Avatar from '../../containers/users/Avatar'
import Countdown from '../layout/countdown'
import AudioStreamer from '../../../libraries/websocket/AudioStreamer'

interface VoiceRecorderProps {
  onRecord: () => void
  onSend: () => void
  onDiscard: () => void
  onAudioChunk: (audioBase64Chunk: string) => void
  size?: 'sm' | 'lg'
  readyToRecord?: boolean
  isProcessingAudio?: boolean // when true we hide discard, volume bars and send icon
}

let audioStreamer: AudioStreamer | null = null

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ size = 'sm', onRecord, onSend, onDiscard, onAudioChunk, readyToRecord = true, isProcessingAudio = false }) => {
  const [isRecording, setIsRecording] = React.useState(false)
  const [recordDisabled, setRecordDisabled] = React.useState(false)
  const [showCountdown, setShowCountdown] = React.useState(false)
  const [isTalking, setIsTalking] = React.useState(false)
  const [videoStream, setVideoStream] = React.useState<MediaStream | null>(null)

  const audioRecorderRef = React.useRef<MediaRecorder | null>(null)
  const videoRecorderRef = React.useRef<MediaRecorder | null>(null)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const isRecordingRef = React.useRef(false)

  React.useEffect(() => {
    return () => {
      stopRecording()
    }
  }, [])

  React.useEffect(() => {
    if(readyToRecord && !isRecordingRef?.current) {
      startRecording()
    }
    isRecordingRef.current = readyToRecord
  }, [readyToRecord])

  const pickMimeType = () => {
    const preferred = [
      'audio/webm;codecs=opus',   // Chrome / Firefox
      'audio/webm',               // fallback WebM
      'audio/mp4;codecs=opus',    // Safari 17+
      'audio/mp4',                // Safari ≤16 (AAC)
    ]
    return preferred.find(t => MediaRecorder.isTypeSupported(t)) || ''
  }

  const buildAudioConstraints = () => {
    const supported = navigator.mediaDevices.getSupportedConstraints()
    const c: MediaTrackConstraints = {
      sampleRate: 24000,        // Optimal for OpenAI
      channelCount: 1,          // Mono only
      echoCancellation: true,   // Essential for preventing feedback
      noiseSuppression: true,   // Reduce background noise
      autoGainControl: true,    // Normalize volume
    }
    if (!supported.sampleRate) { c.sampleRate = 16000 }
    if (supported.noiseSuppression) { c.noiseSuppression = true }
    if (supported.autoGainControl) { c.autoGainControl  = true }

    return c
  }

  const startRecording = async () => {
    const mimeType = pickMimeType()

    try {
      // Get separate audio stream for recording
      const audioConstraints = buildAudioConstraints()

      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints
      })

      let videoStream: MediaStream
      try {
        // Get video stream for display only
        videoStream = await navigator.mediaDevices.getUserMedia({
          video: {
            // width: { ideal: 640 },
            // height: { ideal: 480 },
            facingMode: 'user',
          },
          audio: false,
        })
        videoRecorderRef.current = new MediaRecorder(videoStream)
        setVideoStream(videoStream)
      } catch (error: unknown) {
        // if user doesnt allow video, or doesnt have camera
        // we will show the user avatar instead.
        setVideoStream(null)
      } finally {
        // Set video stream after video element is rendered
        setShowCountdown(true)
        setTimeout(() => {
          if (videoRef.current && videoStream) {
            videoRef.current.srcObject = videoStream
            videoRef.current.play().catch(e => console.warn('Video play failed:', e))
          }
        }, 100)
      }

      audioStreamer = new AudioStreamer(audioStream, audioConstraints.sampleRate as number, {
        onError: (error: string) => console.error(error),
        onSpeechStarted: () => { setIsTalking(true) },
        onSpeechEnded: () => { setIsTalking(false) },
        onAudioChunk: (chunk) => {
          if (!isSendingRef.current && isRecordingRef.current) {
            onAudioChunk(chunk)
          }
        },
      })
      toggleRecording(true)
      // Use only audio stream for MediaRecorder
      audioRecorderRef.current = new MediaRecorder(audioStream, { mimeType })
      audioRecorderRef.current?.start()
      await audioStreamer.start()
    } catch (error) {
      const err = error as Error
      if (err.name === 'NotSupportedError') {
        alert(`This browser can't record ${mimeType}.`)
      } else if (err.name === 'OverconstrainedError') {
        alert('Requested audio/video constraint isn\'t available.')
      } else if (err.name === 'NotAllowedError') {
        alert('Camera and microphone access was denied.')
      } else {
        alert('Unable to access camera and microphone. Please check permissions.')
        console.error(err)
      }

      toggleRecording(false)
    }
  }

  const onClickRecord = () => {
    setRecordDisabled(true)
    onRecord()
  }

  const isSendingRef = React.useRef(false)

  const sendRecording = async () => {
    setRecordDisabled(false)
    isSendingRef.current = true
    onSend()
    await audioStreamer?.stop()
    toggleRecording(false)
    stopMediaRecorder()
    // allow future recordings
    isSendingRef.current = false
  }

  const stopMediaRecorder = () => {
    const stopMediaRecorderTracks = (ref: React.MutableRefObject<MediaRecorder | null>) => {
      if(ref?.current) {
        ref.current.stop()
        ref.current.stream.getTracks().forEach(track => track.stop())
        ref.current = null
      }
    }

    stopMediaRecorderTracks(audioRecorderRef)
    stopMediaRecorderTracks(videoRecorderRef)
  }

  const discardRecording = () => {
    setRecordDisabled(false)
    stopRecording()
    onDiscard()
  }

  const stopRecording = () => {
    audioStreamer?.discard()
    toggleRecording(false)
    stopMediaRecorder()
  }

  const toggleRecording = (recording: boolean) => {
    setIsRecording(recording)
  }

  return (
    <>
      <div className='w-full text-center flex flex-col gap-y-6'>
        <div>
          {(!isRecording || !videoStream) && <Avatar size='xl' hideSubscriptionTag />}
          {(isRecording || showCountdown) && (
            <div className='relative'>
              {showCountdown && (
                <div className={videoStream ? 'absolute left-[50%] -translate-x-[50%] top-[50%] -translate-y-[50%]' : ''}>
                  <Countdown
                    isVisible={showCountdown}
                    onComplete={() => setShowCountdown(false)}
                    color={videoStream ? 'white' : 'deep-green'}
                  />
                </div>
              )}
              {videoStream && (
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className='w-64 h-48 bg-black rounded-lg object-cover mx-auto'
                />
              )}
            </div>
          )}
        </div>
        {!isRecording && !showCountdown && (
          <div>
            <Button
              size={size}
              onClick={onClickRecord}
              outlined
              disabled={recordDisabled || isProcessingAudio}
              icon={<MicrophoneIcon stroke='deep-green' />}
            />
          </div>
        )}
        {isRecording && !showCountdown && !isProcessingAudio && (
          <div className='min-w-lg'>
            <div className='flex items-center justify-around w-lg'>
              <Button
                variant='primary'
                outlined
                type='button'
                icon={<CloseIcon stroke='stroke-deep-green' />}
                size='sm'
                onClick={discardRecording}
              />

              <div className='mx-6'><VoiceActivityBars isActive={isTalking} /></div>

              <Button
                variant='primary'
                icon={<SendIcon stroke='white' />}
                size='sm'
                onClick={sendRecording}
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default VoiceRecorder
